import ChatPage from "@/pages/ChatPage";
import type { Message } from "@/components/chat/MessageBubble";
import type {
  DocumentDownloadLoader,
  DocumentPreviewLoader,
} from "@/components/chat/SourceDocumentLightbox";
import type { ChatSource, ConversationSummary } from "@/lib/api";

const fixtureSources: ChatSource[] = [
  {
    index: 1,
    tag: "Platform Results CY25",
    document_id: "fixture-platform-results",
    document_name: "Platform Results CY25",
    file_type: "pdf",
    file_size: 2840000,
    access_level: "public",
    referenced_in: ["4. Operating Performance", "8. Unit Economics"],
    section: "4.3 CY25 performance summary",
    excerpt:
      "Revenue expansion was supported by higher utilization, new account wins, and improved attach rates across the platform.",
  },
  {
    index: 2,
    tag: "Asset Book Q2 2026",
    document_id: "fixture-asset-book",
    document_name: "Asset Book Q2 2026",
    file_type: "xlsx",
    file_size: 1320000,
    access_level: "public",
    referenced_in: ["5. Asset Portfolio", "5.1 Portfolio Summary"],
    section: "5.1 Portfolio summary",
    excerpt:
      "The portfolio tab ties stabilized yield-on-cost, lease-up status, and near-term capital plan assumptions to each asset.",
  },
  {
    index: 3,
    tag: "MIP Grant: Trent Cameron Top-up 2025-12-23",
    document_id: "fixture-mip-grant",
    document_name: "MIP Grant: Trent Cameron Top-up 2025-12-23",
    file_type: "pdf",
    file_size: 260000,
    sensitivity: 5,
    access_level: "restricted",
    request_access_email: "ir@greenpointpartners.com",
    referenced_in: ["12. Management Incentive Plan"],
    section: "12.2 Individual grant documents",
    excerpt: null,
  },
];

const fixtureConversations: ConversationSummary[] = [
  {
    id: "source-lightbox-harness",
    title: "Source lightbox UAT",
    user_email: "lp@example.com",
    created_at: "2026-05-11T14:00:00.000Z",
    updated_at: "2026-05-11T14:25:00.000Z",
  },
];

const fixtureMessages: Message[] = [
  {
    id: "harness-user-1",
    role: "user",
    content: "Show me the cited source documents for platform performance and the asset book.",
  },
  {
    id: "harness-assistant-1",
    role: "assistant",
    content:
      "Outpost's CY25 performance narrative is grounded in improved utilization and account expansion [Platform Results CY25]. The asset-level assumptions sit in the Q2 portfolio workbook [Asset Book Q2 2026]. Individual MIP grant detail remains request-only [MIP Grant: Trent Cameron Top-up 2025-12-23].",
    sources: fixtureSources,
  },
];

function previewHtml(source: ChatSource): string {
  const citedIn = source.referenced_in?.map((section) => `<li>${section}</li>`).join("") || "";
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <style>
      body {
        margin: 0;
        padding: 40px;
        color: #1a202c;
        background: #ffffff;
        font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        line-height: 1.55;
      }
      .eyebrow {
        color: #1478ff;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }
      h1 {
        margin: 10px 0 18px;
        font-size: 28px;
        line-height: 1.2;
      }
      .panel {
        border: 1px solid #e2e5ed;
        border-radius: 8px;
        padding: 18px;
        background: #f5f7fb;
      }
      table {
        width: 100%;
        margin-top: 22px;
        border-collapse: collapse;
        font-size: 14px;
      }
      th,
      td {
        border: 1px solid #e2e5ed;
        padding: 10px 12px;
        text-align: left;
      }
      th {
        background: #f0f2f7;
      }
    </style>
  </head>
  <body>
    <div class="eyebrow">Harness preview</div>
    <h1>${source.document_name}</h1>
    <div class="panel">
      <strong>${source.section || "Source context"}</strong>
      <p>${source.excerpt || "This restricted document is represented without sensitive detail."}</p>
    </div>
    <table>
      <thead>
        <tr><th>Metric</th><th>Harness value</th><th>Source</th></tr>
      </thead>
      <tbody>
        <tr><td>Preview mode</td><td>Inline lightbox render</td><td>${source.document_name}</td></tr>
        <tr><td>Access</td><td>${source.access_level || "public"}</td><td>${source.file_type?.toUpperCase() || "DOC"}</td></tr>
      </tbody>
    </table>
    ${citedIn ? `<h2>Cited in</h2><ul>${citedIn}</ul>` : ""}
  </body>
</html>`;
}

const previewLoader: DocumentPreviewLoader = async (source) => {
  if (source.access_level === "restricted") {
    return {
      error: "Document access restricted",
      access_level: "restricted",
      document_name: source.document_name,
      sensitivity: source.sensitivity ?? 5,
      request_access_email: source.request_access_email || "ir@greenpointpartners.com",
      message: `This source is available from GreenPoint IR by request.`,
    };
  }

  return {
    document_name: source.document_name,
    preview_url: `data:text/html;charset=utf-8,${encodeURIComponent(previewHtml(source))}`,
    expires_in: 3600,
  };
};

const downloadLoader: DocumentDownloadLoader = async (source) => ({
  document_name: source.document_name,
  download_url: `data:text/plain;charset=utf-8,${encodeURIComponent(source.document_name)}`,
  expires_in: 3600,
});

export default function SourceLightboxHarness() {
  const params = new URLSearchParams(window.location.search);
  const previewIndex = Number(params.get("preview"));
  const initialPreviewSource = Number.isFinite(previewIndex)
    ? fixtureSources.find((source) => source.index === previewIndex) || null
    : null;

  return (
    <ChatPage
      initialConversations={fixtureConversations}
      initialConversationId="source-lightbox-harness"
      initialMessages={fixtureMessages}
      initialSources={fixtureSources}
      initialPreviewSource={initialPreviewSource}
      disableConversationRefresh
      documentPreviewLoader={previewLoader}
      documentDownloadLoader={downloadLoader}
    />
  );
}
